import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Mail, Copy, ExternalLink, User, RefreshCw } from 'lucide-react';
import { getAuthHeaders } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Concierge brand onboarding for Freddy Media ops.
 * Two modes:
 *   - Direct create: enter everything now, optionally set a password.
 *   - Send invite: generate a magic link and (if Mailgun configured) email it.
 * After creation the row exposes "Impersonate" so ops can operate the portal
 * as that brand and run campaigns on their behalf.
 */
export default function AdminBrandsManager() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [mode, setMode] = useState('direct'); // direct | invite
  const [form, setForm] = useState({ name: '', contact_name: '', contact_email: '', portal_password: '', industry: '', website: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/signups`, { headers: getAuthHeaders() })
      .then(r => setBrands(r.data.brands || []))
      .catch(() => toast.error('Failed to load brands'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || !form.contact_email) {
      toast.error('Brand name and contact email are required');
      return;
    }
    setSaving(true);
    try {
      const created = await axios.post(`${API}/admin/brands`, {
        name: form.name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        industry: form.industry,
        website: form.website,
        phone: form.phone,
        portal_password: mode === 'direct' && form.portal_password ? form.portal_password : null,
      }, { headers: getAuthHeaders() });

      if (mode === 'invite') {
        const inv = await axios.post(
          `${API}/admin/brands/${created.data.id}/invite`,
          { portal_url: window.location.origin },
          { headers: getAuthHeaders() },
        );
        setLastInvite(inv.data);
      }
      toast.success(mode === 'direct' ? 'Brand created' : 'Brand created + invite sent');
      setShowAdd(false);
      setForm({ name: '', contact_name: '', contact_email: '', portal_password: '', industry: '', website: '', phone: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const impersonate = async (brand) => {
    try {
      const r = await axios.post(`${API}/admin/brands/${brand.id}/impersonate`, {}, { headers: getAuthHeaders() });
      // Stash the brand token and jump into the portal
      localStorage.setItem('propost_brand_token', r.data.access_token);
      window.open(`${window.location.origin}/portal`, '_blank');
      toast.success(`Impersonating ${r.data.brand_name} — opened in new tab.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impersonate failed');
    }
  };

  const resendInvite = async (brand) => {
    try {
      const inv = await axios.post(
        `${API}/admin/brands/${brand.id}/invite`,
        { portal_url: window.location.origin },
        { headers: getAuthHeaders() },
      );
      setLastInvite(inv.data);
      toast.success(`Invite ${inv.data.delivery_status === 'sent' ? 'emailed' : 'generated'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invite failed');
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-brands-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">Brands</h2>
          <p className="text-xs text-[#9CA3AF]">Concierge onboarding — add brands, send magic-link invites, impersonate to run campaigns on their behalf.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="text-xs px-3 py-2 border border-white/20 text-white hover:bg-white/5 flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="add-brand-btn"
            className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold flex items-center gap-1.5 hover:bg-[#E03030]">
            <Plus className="w-3.5 h-3.5" /> Add brand
          </button>
        </div>
      </div>

      {/* Last invite panel (shown briefly after send) */}
      {lastInvite && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 flex items-start justify-between gap-3 flex-wrap" data-testid="last-invite">
          <div className="text-xs">
            <p className="text-yellow-400 font-mono uppercase mb-1">Invite {lastInvite.delivery_status === 'sent' ? 'emailed' : 'generated (email disabled)'}</p>
            <p className="text-[#9CA3AF]">To <strong className="text-white">{lastInvite.email}</strong> · expires {new Date(lastInvite.expires_at).toLocaleDateString()}</p>
            <p className="text-[10px] text-[#6B7280] mt-1 break-all">{lastInvite.invite_url}</p>
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => { navigator.clipboard.writeText(lastInvite.invite_url); toast.success('Link copied'); }}
              className="text-[10px] px-2 py-1 border border-white/20 text-white hover:bg-white/5 flex items-center gap-1">
              <Copy className="w-3 h-3" /> Copy link
            </button>
            <button onClick={() => setLastInvite(null)}
              className="text-[10px] px-2 py-1 text-[#6B7280] hover:text-white">Dismiss</button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-[#9CA3AF]">Loading…</p>
      ) : brands.length === 0 ? (
        <div className="text-center py-10 bg-[#111827] border border-white/10">
          <p className="text-sm text-[#9CA3AF]">No brands yet. Add your first concierge client above.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {brands.map(b => (
            <div key={b.id} className="bg-[#111827] border border-white/10 p-4 flex items-start justify-between gap-3 flex-wrap" data-testid={`brand-row-${b.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-white truncate">{b.name}</span>
                  {b.verified && <span className="text-[9px] font-mono uppercase text-[#10B981] bg-[#10B981]/10 px-1.5 py-0.5">verified</span>}
                  {b.payment_on_file && <span className="text-[9px] font-mono uppercase text-[#FF4343] bg-[#FF4343]/10 px-1.5 py-0.5">card on file</span>}
                  {b.onboarding_completed_at && <span className="text-[9px] font-mono uppercase text-[#10B981] bg-[#10B981]/10 px-1.5 py-0.5">onboarded</span>}
                  {!b.portal_password_hash && !b.onboarding_completed_at && (
                    <span className="text-[9px] font-mono uppercase text-yellow-400 bg-yellow-500/10 px-1.5 py-0.5">invite pending</span>
                  )}
                </div>
                <p className="text-xs text-[#9CA3AF] mt-1">{b.contact_name || '—'} · {b.portal_email || b.contact_email}</p>
                <p className="text-[10px] text-[#6B7280] font-mono mt-0.5">{b.id} · {b.industry || 'no industry'} · created {new Date(b.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button onClick={() => resendInvite(b)} data-testid={`send-invite-${b.id}`}
                  className="text-[11px] px-3 py-1.5 border border-white/20 text-white hover:bg-white/5 flex items-center gap-1">
                  <Mail className="w-3 h-3" /> Send invite
                </button>
                <button onClick={() => impersonate(b)} data-testid={`impersonate-${b.id}`}
                  className="text-[11px] px-3 py-1.5 border border-[#FF4343] text-[#FF4343] hover:bg-[#FF4343]/10 flex items-center gap-1">
                  <User className="w-3 h-3" /> Impersonate <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => !saving && setShowAdd(false)}>
          <div className="bg-[#111827] border border-white/10 p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="add-brand-modal">
            <h3 className="font-display text-lg font-bold text-white mb-1">Add a brand</h3>
            <p className="text-xs text-[#9CA3AF] mb-4">
              {mode === 'direct'
                ? 'Enter everything now. Optionally set a password so you can log in as them straight away.'
                : "We'll email them a magic link. They'll set their own password + save a credit card during onboarding."}
            </p>
            <div className="flex gap-2 mb-4">
              <button onClick={() => setMode('direct')} data-testid="mode-direct"
                className={`text-xs px-3 py-1.5 border ${mode === 'direct' ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#9CA3AF]'}`}>
                Direct create
              </button>
              <button onClick={() => setMode('invite')} data-testid="mode-invite"
                className={`text-xs px-3 py-1.5 border ${mode === 'invite' ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#9CA3AF]'}`}>
                Send onboarding link
              </button>
            </div>
            <div className="space-y-3">
              {[
                { key: 'name', label: 'Brand name', required: true, placeholder: 'e.g. Acme Motors' },
                { key: 'contact_name', label: 'Contact name', placeholder: 'e.g. Jane Doe' },
                { key: 'contact_email', label: 'Contact email', required: true, type: 'email', placeholder: 'jane@acme.com' },
                { key: 'industry', label: 'Industry', placeholder: 'e.g. automotive' },
                { key: 'website', label: 'Website', placeholder: 'https://acme.com' },
                { key: 'phone', label: 'Phone', placeholder: 'optional' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">{f.label}{f.required && ' *'}</label>
                  <input type={f.type || 'text'} value={form[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    data-testid={`brand-form-${f.key}`}
                    className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                </div>
              ))}
              {mode === 'direct' && (
                <div>
                  <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Portal password (optional)</label>
                  <input type="password" value={form.portal_password}
                    onChange={e => setForm({ ...form, portal_password: e.target.value })}
                    placeholder="8+ chars — leave blank to require an invite later"
                    data-testid="brand-form-password"
                    className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
                </div>
              )}
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => setShowAdd(false)} disabled={saving}
                className="text-xs px-4 py-2 border border-white/20 text-white hover:bg-white/5">Cancel</button>
              <button onClick={submit} disabled={saving}
                data-testid="submit-add-brand"
                className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] disabled:opacity-50">
                {saving ? 'Saving…' : mode === 'direct' ? 'Create brand' : 'Create + send invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
